import { apiClient, parseCurrencyHeaderToCents, parseOptionalIntHeader } from './client.js'
import type { DonateDonationsSearchRequest, DonationResponse, DonationSearchEntity, DonationSummary, GiftVariantHistoryEntry } from '../models/donation.js'
import type { DonationActivityEvent, DonationActivityPage } from '../models/donation-activity.js'
import type { PaymentDetail, PaymentSearchEntity, PaymentsSearchRequest, PaymentsSearchResponse } from '../models/payment.js'
import { ElasticQueryBuilder } from '../helpers/elastic-query-builder.js';
import { logger } from '../logging/logger.js';
import { FetchOptions } from '../models/common.js';

export interface DonateSearchOptions {
  signal?: AbortSignal
}

export async function searchDonationsWithResponseDetails(
  body: DonateDonationsSearchRequest,
  options: DonateSearchOptions = {},
): Promise<{ data: DonationSearchEntity[]; response: Response }> {
  return await apiClient.postWithResponse<DonationSearchEntity[]>(
    '/donate/v1/donations/search',
    body,
    { withAuth: true, signal: options.signal },
  )
}

export async function searchDonations(
  body: DonateDonationsSearchRequest,
  options: DonateSearchOptions = {},
): Promise<DonationSearchEntity[]> {
  return await apiClient.post<DonationSearchEntity[]>(
    '/donate/v1/donations/search',
    body,
    { withAuth: true, signal: options.signal },
  )
}

export async function fetchDonationSummary(memberId: number, options?: FetchOptions): Promise<DonationSummary | null> {
  try {
    const qb = new ElasticQueryBuilder()
    const id = memberId

    qb.addFilter({
      bool: {
        should: [{ term: { member_id: id } }, { term: { 'member.id': id } }],
        minimum_should_match: 1,
      },
    })

    // Match Donations list default statuses: pending + completed + active
    qb.addFilter({ terms: { status: ['pending', 'completed', 'active'] } })

    const { response } = await searchDonationsWithResponseDetails(
      { q: qb.toJson(), page: 1, per_page: 1 },
      { signal: options?.signal },
    )

    const count = parseOptionalIntHeader(response.headers, 'x-total')
    const totalAmountCents = parseCurrencyHeaderToCents(response.headers, 'x-total-amount')

    return { count, totalAmountCents }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error
    }
    logger.error('Failed to load donation summary', { memberId, error })
    return null
  }
}

export async function searchPaymentsWithResponseDetails(
  body: PaymentsSearchRequest,
  options: DonateSearchOptions = {},
): Promise<{ data: PaymentSearchEntity[]; response: Response }> {
  return await apiClient.postWithResponse<PaymentSearchEntity[]>(
    '/donate/v1/payments/search',
    body,
    { withAuth: true, signal: options.signal },
  )
}

export async function searchPayments(
  body: PaymentsSearchRequest,
  options: DonateSearchOptions = {},
): Promise<PaymentsSearchResponse> {
  const { data, response } = await searchPaymentsWithResponseDetails(body, options)
  const total = parseOptionalIntHeader(response.headers, 'x-total') ?? data.length
  return { data, total }
}

export interface CancelDonationRequest {
  cancel_reason_code: string
}

export interface CancelDonationOptions {
  signal?: AbortSignal
  timeout?: number
}

export async function cancelDonation(
  donationId: number,
  body: CancelDonationRequest,
  options: CancelDonationOptions = {},
): Promise<DonationResponse> {
  return await apiClient.put<DonationResponse>(
    `/donate/v1/donations/${donationId}/cancel`,
    body,
    { withAuth: true, signal: options.signal, timeout: options.timeout },
  )
}

export async function getPayment(
  paymentId: string,
  options: DonateSearchOptions = {},
): Promise<PaymentDetail> {
  return await apiClient.get<PaymentDetail>(
    `/donate/v1/payments/${paymentId}`,
    { withAuth: true, signal: options.signal },
  )
}

export async function refundPayment(
  paymentId: string,
  options: DonateSearchOptions = {},
): Promise<PaymentDetail> {
  return await apiClient.put<PaymentDetail>(
    `/donate/v1/payments/${paymentId}/refund`,
    {},
    { withAuth: true, signal: options.signal },
  )
}

export interface ReassignCampaignRequest {
  campaign_id: number
}

/**
 * Move a donation's campaign attribution.
 * Donate API: `PUT /donate/v1/donations/:id/campaign` (campaign_ops + donations).
 *
 * Existing payments keep their own snapshotted campaign — see
 * `reassignPaymentCampaign` to move those individually.
 */
export async function reassignDonationCampaign(
  donationId: number,
  body: ReassignCampaignRequest,
  options: CancelDonationOptions = {},
): Promise<DonationResponse> {
  return await apiClient.put<DonationResponse>(
    `/donate/v1/donations/${donationId}/campaign`,
    body,
    { withAuth: true, signal: options.signal, timeout: options.timeout },
  )
}

/**
 * Move a single payment's campaign attribution.
 * Donate API: `PUT /donate/v1/payments/:id/campaign` (campaign_ops + donations).
 *
 * The parent donation is unaffected.
 */
export async function reassignPaymentCampaign(
  paymentId: string,
  body: ReassignCampaignRequest,
  options: DonateSearchOptions = {},
): Promise<PaymentDetail> {
  return await apiClient.put<PaymentDetail>(
    `/donate/v1/payments/${encodeURIComponent(paymentId)}/campaign`,
    body,
    { withAuth: true, signal: options.signal },
  )
}

export interface MemsysExportFilters {
  status: string
  type: string
  payment_method: string
  start_date: string
  end_date: string
}

export interface CreateMemsysExportRequest {
  filters: MemsysExportFilters
  recipients: string
}

export async function createMemsysExport(
  payload: CreateMemsysExportRequest,
  options?: { signal?: AbortSignal },
): Promise<void> {
  await apiClient.post('/donate/v1/exports/memsys', payload, { withAuth: true, signal: options?.signal })
}

export interface CreateTestimonialsUpdatedDonationsExportRequest {
  recipients: string
}

export async function createTestimonialsUpdatedDonationsExport(
  payload: CreateTestimonialsUpdatedDonationsExportRequest,
  options?: { signal?: AbortSignal },
): Promise<void> {
  await apiClient.post('/donate/v1/exports/testimonials/updated-donations', payload, { withAuth: true, signal: options?.signal })
}

export interface Testimonial {
  id: number
  member_first_name: string
  member_city: string | null
  amount: number
  monthly: boolean
  dedicated_to: string
  created_at: string
}

export async function fetchTestimonials(options?: { signal?: AbortSignal }): Promise<Testimonial[]> {
  return await apiClient.get<Testimonial[]>('/donate/v1/exports/testimonials', { withAuth: true, signal: options?.signal })
}

export async function fetchDonationGiftHistory(
  donationId: number,
  options?: { signal?: AbortSignal },
): Promise<GiftVariantHistoryEntry[]> {
  const response = await apiClient.get<{ gift_variant_history: GiftVariantHistoryEntry[] }>(
    `/donate/v1/donations/${donationId}/gift_history`,
    { withAuth: true, signal: options?.signal },
  )
  return response.gift_variant_history
}

export interface MemberActivityOptions {
  page?: number
  perPage?: number
  signal?: AbortSignal
}

export async function getMemberActivity(
  memberId: number,
  options: MemberActivityOptions = {},
): Promise<DonationActivityPage> {
  const toPositiveInt = (value: number | undefined, fallback: number): number =>
    typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : fallback
  const page = toPositiveInt(options.page, 1)
  const perPage = toPositiveInt(options.perPage, 25)

  const query = new URLSearchParams({ page: String(page), per_page: String(perPage) }).toString()

  const { data, response } = await apiClient.getWithResponse<DonationActivityEvent[]>(
    `/donate/v1/members/${memberId}/activity?${query}`,
    { withAuth: true, signal: options.signal },
  )

  const total = parseOptionalIntHeader(response.headers, 'x-total') ?? data.length
  const totalPages = parseOptionalIntHeader(response.headers, 'x-total-pages') ?? page

  return { data, page, perPage, total, totalPages, hasMore: page < totalPages }
}
